/**
 * Background Sync for the offline scan queue (design "Clients → Operator_Console").
 *
 * The page registers a one-off `sync` with tag {@link SCAN_SYNC_TAG} whenever it queues a
 * scan (Chromium-based browsers; elsewhere this is a no-op and the `online`/reconnect
 * trigger does the work). When the browser fires the sync, the service worker cannot
 * submit by itself: access tokens live only in page memory (oidc-client-ts in-memory
 * store), so the worker posts {@link FLUSH_SCAN_QUEUE_MESSAGE} to every open console
 * window, which flushes with its own session. With no window open the sync completes
 * without submitting; the queue stays in IndexedDB and flushes the next time the console
 * opens and reaches the API.
 *
 * Pure module: imported by both the page bundle and `src/sw/sw.ts`.
 */
export const SCAN_SYNC_TAG = 'scan-queue';
export const FLUSH_SCAN_QUEUE_MESSAGE = 'FLUSH_SCAN_QUEUE';

export interface FlushScanQueueMessage {
  type: typeof FLUSH_SCAN_QUEUE_MESSAGE;
}

export function isFlushMessage(data: unknown): data is FlushScanQueueMessage {
  return (data as { type?: unknown } | null)?.type === FLUSH_SCAN_QUEUE_MESSAGE;
}

/** Service-worker side: asks every open window to flush; returns how many were asked. */
export async function notifyClientsToFlush(clients: {
  matchAll(options: { type: 'window'; includeUncontrolled: boolean }): Promise<readonly { postMessage(m: unknown): void }[]>;
}): Promise<number> {
  const windows = await clients.matchAll({ type: 'window', includeUncontrolled: true });
  const message: FlushScanQueueMessage = { type: FLUSH_SCAN_QUEUE_MESSAGE };
  for (const w of windows) w.postMessage(message);
  return windows.length;
}

interface SyncManagerLike {
  register(tag: string): Promise<void>;
}

type ServiceWorkerContainerLike = Pick<ServiceWorkerContainer, 'getRegistration' | 'addEventListener' | 'removeEventListener'>;

/** Page side: registers the one-off sync where supported. Never throws. */
export async function requestScanSync(container: ServiceWorkerContainerLike | undefined): Promise<boolean> {
  try {
    const registration = await container?.getRegistration();
    const sync = (registration as { sync?: SyncManagerLike } | undefined)?.sync;
    if (!sync) return false;
    await sync.register(SCAN_SYNC_TAG);
    return true;
  } catch {
    // Permission denied, no active worker, private mode: the reconnect trigger remains.
    return false;
  }
}

/** Page side: calls `flush` when the service worker relays a sync. Returns an unsubscribe. */
export function listenForFlushRequests(container: ServiceWorkerContainerLike | undefined, flush: () => void): () => void {
  if (!container) return () => undefined;
  const onMessage = (event: Event) => {
    if (isFlushMessage((event as MessageEvent).data)) flush();
  };
  container.addEventListener('message', onMessage);
  return () => container.removeEventListener('message', onMessage);
}
